import { customLink, calendarDate } from "../../utils/customPageLinks";
import React, { useMemo, useRef, useState } from "react";

const sectionTypes = new Set(['header','text','image','imageText','imageRow','button','eventsCalendar']);
const strings = ['id','title','subtitle','content','label','alt','imageUrl','url','link'];
const records = value => Array.isArray(value) ? value.filter(item => item && typeof item === 'object' && !Array.isArray(item)) : [];
function publicSections(value) {
  return records(value).filter(section => sectionTypes.has(section.type)).map(section => ({
    type:section.type, ...Object.fromEntries(strings.map(key => [key, typeof section[key] === 'string' ? section[key] : ''])),
    ...Object.fromEntries(['images','links','alts'].map(key => [key, Array.isArray(section[key]) ? section[key].map(item => typeof item === 'string' ? item : '') : []])),
    events:records(section.events).map(event => Object.fromEntries(['title','date','time','location','link'].map(key => [key,typeof event[key] === 'string' ? event[key] : '']))),
  }));
}
function PageImage({src, alt, className}) {
  const [failed, setFailed] = useState(false);
  return failed ? <p className="rounded-lg border border-bookish-purple/20 bg-bookish-cream p-5 text-bookish-purple">{alt ? `Image unavailable: ${alt}` : 'Image unavailable'}</p> : <img src={src} alt={alt} className={className} loading="lazy" onError={() => setFailed(true)} />;
}

const toDateKey = calendarDate;
const getAnchorProps = value => { const href = customLink(value); return href ? { href, ...(href.startsWith('http') ? { target: '_blank', rel: 'noopener noreferrer' } : {}) } : { 'aria-disabled': true }; };

const toDisplayDate = (value) => {
  const key = toDateKey(value);
  if (!key) return "";
  const d = new Date(`${key}T00:00:00`);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const buildMonthGrid = (monthDate) => {
  const year = monthDate.getFullYear();
  const month = monthDate.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const days = [];

  const startPadding = (firstDay.getDay() + 6) % 7;
  for (let i = 0; i < startPadding; i += 1) {
    days.push(null);
  }

  for (let d = 1; d <= lastDay.getDate(); d += 1) {
    days.push(new Date(year, month, d));
  }

  while (days.length % 7 !== 0) days.push(null);
  return days;
};

const MiniCalendar = ({ value, onChange, eventDateSet }) => {
  const [monthDate, setMonthDate] = useState(new Date(value.getFullYear(), value.getMonth(), 1));
  const days = useMemo(() => buildMonthGrid(monthDate), [monthDate]);

  return (
    <div className="border border-bookish-purple/20 rounded-lg p-3 bg-white space-y-2">
      <div className="flex items-center justify-between">
        <button type="button" aria-label="Previous month" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() - 1, 1))} className="text-sm px-2 py-1 min-h-[44px] rounded border">
          Prev
        </button>
        <p className="font-semibold">{monthDate.toLocaleDateString(undefined, { month: "long", year: "numeric" })}</p>
        <button type="button" aria-label="Next month" onClick={() => setMonthDate(new Date(monthDate.getFullYear(), monthDate.getMonth() + 1, 1))} className="text-sm px-2 py-1 min-h-[44px] rounded border">
          Next
        </button>
      </div>
      <div className="grid grid-cols-7 gap-1 text-xs text-center text-bookish-purpleAccent">
        {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>
      <div className="grid grid-cols-7 gap-1">
        {days.map((day, idx) => {
          if (!day) return <div key={idx} className="h-9" />;
          const key = toDateKey(day);
          const hasEvent = eventDateSet.has(key);
          const isActive = key === toDateKey(value);
          return (
            <button
              key={key}
              aria-label={`${toDisplayDate(day)}${hasEvent ? ", has events" : ""}`}
              aria-pressed={isActive}
              onClick={() => onChange(day)}
              type="button"
              className={`min-h-[44px] rounded text-sm border ${isActive ? "bg-bookish-royal text-white" : "bg-white"} ${hasEvent ? "ring-2 ring-bookish-purpleAccent" : ""}`}
            >
              {day.getDate()}
            </button>
          );
        })}
      </div>
    </div>
  );
};

const EventsCalendarSection = ({ section, editable = false, onChange }) => {
  const events = useMemo(() => Array.isArray(section.events) ? section.events : [], [section.events]);
  const [selectedDate, setSelectedDate] = useState(new Date());

  const eventsByDate = useMemo(() => {
    const map = new Map();
    events.forEach((event) => {
      const key = toDateKey(event.date);
      if (!key) return;
      const list = map.get(key) || [];
      list.push(event);
      map.set(key, list);
    });
    return map;
  }, [events]);

  const activeEvents = eventsByDate.get(toDateKey(selectedDate)) || [];

  return (
    <div className="space-y-3">
      <MiniCalendar value={selectedDate} onChange={setSelectedDate} eventDateSet={new Set(eventsByDate.keys())} />
      {editable && (
        <div className="space-y-2 border border-bookish-purple/20 rounded-lg p-3 bg-bookish-white/70">
          <p className="text-sm font-semibold uppercase tracking-wide text-bookish-purpleAccent">Edit Events</p>
          {events.map((event, eventIdx) => (
            <div key={eventIdx} className="grid md:grid-cols-2 gap-2 border border-bookish-purple/10 rounded p-2">
              <input
                value={event.title || ""}
                onChange={(e) =>
                  onChange({
                    ...section,
                    events: events.map((item, i) => (i === eventIdx ? { ...item, title: e.target.value } : item)),
                  })
                }
                aria-label="Event title" placeholder="Event title"
                className="border rounded px-2 py-1"
              />
              <input
                aria-label="Event date"
                type="date"
                value={event.date || ""}
                onChange={(e) =>
                  onChange({
                    ...section,
                    events: events.map((item, i) => (i === eventIdx ? { ...item, date: e.target.value } : item)),
                  })
                }
                className="border rounded px-2 py-1"
              />
              <input
                value={event.time || ""}
                onChange={(e) =>
                  onChange({
                    ...section,
                    events: events.map((item, i) => (i === eventIdx ? { ...item, time: e.target.value } : item)),
                  })
                }
                aria-label="2:00 PM" placeholder="2:00 PM"
                className="border rounded px-2 py-1"
              />
              <input
                value={event.location || ""}
                onChange={(e) =>
                  onChange({
                    ...section,
                    events: events.map((item, i) => (i === eventIdx ? { ...item, location: e.target.value } : item)),
                  })
                }
                aria-label="Location" placeholder="Location"
                className="border rounded px-2 py-1"
              />
              <input
                value={event.link || ""}
                onChange={(e) =>
                  onChange({
                    ...section,
                    events: events.map((item, i) => (i === eventIdx ? { ...item, link: e.target.value } : item)),
                  })
                }
                aria-label="Event link" placeholder="Event link"
                className="md:col-span-2 border rounded px-2 py-1"
              />
              <button onClick={()=>onChange({...section,events:events.filter((_,i)=>i!==eventIdx)})} className="text-red-700">Remove event {eventIdx+1}</button>
            </div>
          ))}
          <button
            onClick={() =>
              onChange({
                ...section,
                events: [...events, { title: "", date: "", time: "", location: "", link: "" }],
              })
            }
            className="text-sm text-bookish-royal hover:underline"
          >
            + Add Event
          </button>
        </div>
      )}
      <div className="border border-bookish-purple/20 rounded-lg p-3 bg-white">
        <p className="font-semibold text-bookish-purple mb-2">Events on {toDisplayDate(selectedDate)}</p>
        {activeEvents.length === 0 && <p className="text-sm text-bookish-purpleAccent">No events.</p>}
        <div className="space-y-2">
          {activeEvents.map((event, idx) => (
            <div key={`${event.title}-${idx}`} className="border border-bookish-purple/10 rounded p-2">
              <p className="font-medium">{event.title || "Untitled Event"}</p>
              <p className="text-sm text-bookish-purpleAccent">{event.time || "Time TBD"} • {event.location || "Location TBD"}</p>
              {event.link && (
                <a {...(editable ? {'aria-disabled': true} : getAnchorProps(event.link))} className="text-sm text-bookish-royal hover:underline">
                  Event link
                </a>
              )}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const sectionControlButton = "text-xs min-h-[44px] px-2 py-1 rounded border border-bookish-purple/25 hover:bg-bookish-purple/10";

const SectionControls = ({ idx, count, onDelete, onMoveUp, onMoveDown }) => (
  <div className="flex flex-wrap items-center justify-end gap-2 pb-2 border-b border-bookish-purple/10 mb-3">
    <button onClick={() => onMoveUp(idx)} disabled={idx === 0} className={`${sectionControlButton} disabled:opacity-40`}>Move Up</button>
    <button onClick={() => onMoveDown(idx)} disabled={idx === count - 1} className={`${sectionControlButton} disabled:opacity-40`}>Move Down</button>
    <button onClick={() => onDelete(idx)} className={`${sectionControlButton} text-red-700 border-red-200 hover:bg-red-50`}>Delete</button>
  </div>
);

const UploadField = ({ label, onSelect, uploading, className = "" }) => {
  const inputRef = useRef(null);
  return (
    <div className={className}>
      <input ref={inputRef} type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(e) => { onSelect(e.target.files?.[0]); e.target.value = ""; }} />
      <button
        type="button"
        disabled={uploading}
        onClick={() => inputRef.current?.click()}
        className="w-full rounded-md border border-bookish-purple/20 px-3 py-2 text-left text-sm text-bookish-purple hover:bg-bookish-purple/5 disabled:opacity-50"
      >
        {uploading ? "Uploading..." : label}
      </button>
    </div>
  );
};

const renderSection = (section, idx, options = {}) => {
  const { editable, onSectionChange, onDelete, onMoveUp, onMoveDown, count, onImageUpload, uploadingSection } = options;
  if (!editable && section.type === 'button' && (!section.label || !customLink(section.link))) return null;
  if (!editable && section.type === 'image' && !customLink(section.imageUrl || section.url, true)) return null;
  const sectionImage = customLink(section.imageUrl || section.url || "", true);
  const wrapImageLink = (url, content) => (url ? <a {...(editable ? {'aria-disabled': true} : getAnchorProps(url))}>{content}</a> : content);
  const wrap = (content) => (
    <section key={section.id || idx} className={editable ? " min-w-0 p-2 md:p-4 space-y-3 bg-bookish-white" : "break-words py-3"}>
      {editable && <SectionControls idx={idx} count={count} onDelete={onDelete} onMoveUp={onMoveUp} onMoveDown={onMoveDown} />}
      {content}
    </section>
  );

  switch (section.type) {
    case "header":
      return wrap(
        <div className="space-y-2">
          {editable ? (
            <textarea rows={2} value={section.title || ""} onChange={(e) => onSectionChange(idx, { ...section, title: e.target.value })} aria-label="Header title" placeholder="Header title" className="w-full resize-y text-xl md:text-3xl font-serif font-bold text-bookish-purple border rounded px-3 py-2" />
          ) : (
            <h2 className="text-4xl font-serif font-bold text-bookish-purple">{section.title}</h2>
          )}
          {editable ? (
            <input value={section.subtitle || ""} onChange={(e) => onSectionChange(idx, { ...section, subtitle: e.target.value })} aria-label="Subtitle" placeholder="Subtitle" className="w-full border rounded px-3 py-2 text-bookish-purpleAccent" />
          ) : (
            section.subtitle && <p className="text-bookish-purpleAccent">{section.subtitle}</p>
          )}
        </div>
      );
    case "text":
      return wrap(
        editable ? (
          <textarea value={section.content || ""} onChange={(e) => onSectionChange(idx, { ...section, content: e.target.value })} rows={5} className="w-full border rounded px-3 py-2 text-bookish-purple leading-relaxed" aria-label="Text content..." placeholder="Text content..." />
        ) : (
          <div className="text-bookish-purple leading-relaxed whitespace-pre-wrap">{section.content}</div>
        )
      );
    case "image":
      return wrap(
        <div className="space-y-2">
          {sectionImage
            ? wrapImageLink(section.link, <PageImage key={sectionImage} src={sectionImage} alt={section.alt || ""} className="w-full rounded-lg border border-bookish-purple/20" />)
            : <div className="h-48 rounded-lg bg-bookish-purple/5 border border-bookish-purple/20" />}
          {editable && (
            <div className="grid md:grid-cols-2 gap-2">
              <UploadField label="Upload image" uploading={uploadingSection === `${idx}`} onSelect={(file) => onImageUpload?.(idx, null, file)} />
              <input aria-label="Image description" value={section.alt || ""} onChange={e=>onSectionChange(idx,{...section,alt:e.target.value})} placeholder="Image description (optional)" className="w-full border rounded px-3 py-2" />
              <input value={section.link || ""} onChange={(e) => onSectionChange(idx, { ...section, link: e.target.value })} aria-label="Image link (exact URL)" placeholder="Image link (exact URL)" className="w-full border rounded px-3 py-2" />
            </div>
          )}
        </div>
      );
    case "imageText":
      return wrap(
        <div className="grid md:grid-cols-2 gap-4 items-start">
          {sectionImage
            ? wrapImageLink(section.link, <PageImage key={sectionImage} src={sectionImage} alt={section.alt || ""} className="w-full rounded-lg border border-bookish-purple/20" />)
            : editable ? <div className="h-48 rounded-lg bg-bookish-purple/5" /> : null}
          {editable ? (
            <textarea value={section.content || ""} onChange={(e) => onSectionChange(idx, { ...section, content: e.target.value })} rows={6} aria-label="Image + text section" placeholder="Image + text section" className="w-full border rounded px-3 py-2 text-bookish-purple" />
          ) : (
            <p className="text-bookish-purple whitespace-pre-wrap">{section.content}</p>
          )}
          {editable && (
            <div className="md:col-span-2 grid md:grid-cols-2 gap-2">
              <UploadField label="Upload image" uploading={uploadingSection === `${idx}`} onSelect={(file) => onImageUpload?.(idx, null, file)} />
              <input aria-label="Image description" value={section.alt || ""} onChange={e=>onSectionChange(idx,{...section,alt:e.target.value})} placeholder="Image description (optional)" className="w-full border rounded px-3 py-2" />
              <input value={section.link || ""} onChange={(e) => onSectionChange(idx, { ...section, link: e.target.value })} aria-label="Image link (exact URL)" placeholder="Image link (exact URL)" className="w-full border rounded px-3 py-2" />
            </div>
          )}
        </div>
      );
    case "imageRow":
      return wrap(
        <div className="space-y-2">
          <div className="grid md:grid-cols-3 gap-3">
            {(section.images || []).map((url, imageIdx) => (
              <div key={imageIdx} className="rounded-lg border border-bookish-purple/20 bg-bookish-purple/5 min-h-28 overflow-hidden">
                {customLink(url, true)
                  ? wrapImageLink((section.links || [])[imageIdx], <PageImage key={customLink(url, true)} src={customLink(url, true)} alt={section.alts?.[imageIdx] || ""} className="w-full h-full object-cover" />)
                  : editable ? <div className="h-28" /> : null}
              </div>
            ))}
          </div>
          {editable && (
            <div className="grid md:grid-cols-3 gap-2">
              {(section.images || []).map((img, imageIdx) => (
                <div key={imageIdx} className="space-y-2">
                  <UploadField
                    label={`Upload image ${imageIdx + 1}`}
                    uploading={uploadingSection === `${idx}-${imageIdx}`}
                    onSelect={(file) => onImageUpload?.(idx, imageIdx, file)}
                  />
                  <input aria-label={`Image ${imageIdx + 1} description`} value={section.alts?.[imageIdx] || ''} onChange={e=>onSectionChange(idx,{...section,alts:section.images.map((_,i)=>i===imageIdx?e.target.value:section.alts?.[i]||'')})} className="w-full border rounded px-3 py-2" />
                  <input
                    value={(section.links || [])[imageIdx] || ""}
                    onChange={(e) =>
                      onSectionChange(idx, {
                        ...section,
                        links: (section.links || ["", "", ""]).map((link, i) => (i === imageIdx ? e.target.value : link)),
                      })
                    }
                    aria-label={`Image ${imageIdx + 1} link`} placeholder={`Image ${imageIdx + 1} link`}
                    className="w-full border rounded px-3 py-2"
                  />
                </div>
              ))}
            </div>
          )}
        </div>
      );
    case "button": {

      return wrap(
        <div className="space-y-2">
          <a
            {...(editable ? { 'aria-disabled': true } : section.link ? getAnchorProps(section.link) : { 'aria-disabled': true })}
            className="inline-flex items-center rounded-md bg-bookish-royal text-white px-4 py-2 hover:bg-bookish-purple transition"
          >
            {section.label || "Button"}
          </a>
          {editable && (
            <div className="grid md:grid-cols-2 gap-2">
              <input value={section.label || ""} onChange={(e) => onSectionChange(idx, { ...section, label: e.target.value })} aria-label="Button label" placeholder="Button label" className="w-full border rounded px-3 py-2" />
              <input value={section.link || ""} onChange={(e) => onSectionChange(idx, { ...section, link: e.target.value })} aria-label="Button link" placeholder="Button link" className="w-full border rounded px-3 py-2" />
            </div>
          )}
        </div>
      );
    }
    case "eventsCalendar":
      return wrap(<EventsCalendarSection section={section} editable={editable} onChange={(next) => onSectionChange(idx, next)} />);
    default:
      return null;
  }
};

const CustomPageRenderer = ({ page, editable = false, onSectionChange, onDeleteSection, onMoveSectionUp, onMoveSectionDown, onImageUpload, uploadingSection = "" }) => {
  const sections = editable ? (Array.isArray(page?.sections) ? page.sections : []) : publicSections(page?.sections);

  return (
    <div onClickCapture={e=>{if(editable && e.target.closest("a"))e.preventDefault();}} className="max-w-4xl mx-auto p-0 md:p-2 space-y-0 shadow-sm">
      {sections.map((section, idx) =>
        renderSection(section, idx, {
          editable,
          count: sections.length,
          onSectionChange,
          onDelete: onDeleteSection,
          onMoveUp: onMoveSectionUp,
          onMoveDown: onMoveSectionDown,
          onImageUpload,
          uploadingSection,
        })
      )}
    </div>
  );
};

export default CustomPageRenderer;
